import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../lib/api";
import { trackClick } from "../lib/analytics";
import {
  Wifi, Globe, Search, Plug, Cpu, Network, ArrowRight, Copy, Check,
  Wrench, ChevronDown, Download, Loader2, ExternalLink,
} from "lucide-react";

/* ==========================================================
 * /tools — index of free utilities
 * ==========================================================*/

const TOOLS = [
  { slug: "wifi-qr",             name: "Wi-Fi QR generator",  desc: "Print a QR code guests can scan to join your Wi-Fi.", Icon: Wifi },
  { slug: "dns-lookup",          name: "DNS lookup",          desc: "Resolve A, AAAA, MX, TXT, NS records from any domain.", Icon: Globe },
  { slug: "whois",               name: "WHOIS lookup",        desc: "Who owns a domain? When does it expire?", Icon: Search },
  { slug: "port-check",          name: "Open port checker",   desc: "Test if a TCP port is open on any host.", Icon: Plug },
  { slug: "mac-lookup",          name: "MAC address lookup",  desc: "Find the vendor for any network hardware.", Icon: Cpu },
  { slug: "subnet-calculator",   name: "Subnet calculator",   desc: "Break down IPv4 CIDR ranges instantly.", Icon: Network },
];

export function ToolsIndex() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 py-8 sm:py-12">
      <Helmet>
        <title>Free network tools · Wi-Fi QR, DNS, WHOIS, Port &amp; MAC lookup · SpeedTestTools</title>
        <meta name="description" content="Six free network utilities that run in your browser: Wi-Fi QR code generator, DNS lookup, WHOIS, port checker, MAC vendor lookup, and IPv4 subnet calculator. No sign-up." />
        <link rel="canonical" href="https://speedtesttools.com/tools" />
      </Helmet>
      <header className="mb-8">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--cobalt-bright)] mb-2">/ Free tools</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Network utilities that just work</h1>
        <p className="mt-3 text-sm sm:text-base text-[var(--text-dim)] max-w-2xl">
          Six focused tools for network setup and troubleshooting. No sign-up, no ads on the tools themselves, no upsell.
        </p>
      </header>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tools-grid">
        {TOOLS.map((t) => (
          <Link
            key={t.slug}
            to={`/tools/${t.slug}`}
            onClick={() => trackClick(`tool-open-${t.slug}`)}
            className="glass rounded-2xl p-5 lift group flex items-start gap-4"
            data-testid={`tool-card-${t.slug}`}
          >
            <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#4A78FF] to-[#2D5AFF] shrink-0" style={{ boxShadow: "0 0 32px -8px rgba(61,107,255,0.5)" }}>
              <t.Icon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-base font-semibold">{t.name}</h3>
              <p className="text-sm text-[var(--text-dim)] mt-1 leading-relaxed">{t.desc}</p>
              <div className="mt-3 text-sm text-[var(--cobalt-bright)] flex items-center gap-1 group-hover:gap-2 transition-all">
                Open <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ==========================================================
 * Shared page shell
 * ==========================================================*/

function ToolShell({ slug, title, description, canonical, children, testid }) {
  const meta = TOOLS.find((t) => t.slug === slug);
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-5 lg:px-8 py-8 sm:py-12">
      <Helmet>
        <title>{`${title} · SpeedTestTools`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={`${title} · SpeedTestTools`} />
        <meta property="og:description" content={description} />
      </Helmet>
      <Link to="/tools" className="inline-flex items-center gap-1 text-xs font-mono text-[var(--text-muted)] hover:text-white transition mb-4"><Wrench className="w-3 h-3" /> all tools</Link>
      <header className="mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">{meta?.name || title}</h1>
        <p className="mt-2 text-sm text-[var(--text-dim)]">{description}</p>
      </header>
      <section className="glass rounded-2xl p-5 sm:p-6" data-testid={testid}>{children}</section>
    </div>
  );
}

/* ==========================================================
 * Wi-Fi QR
 * ==========================================================*/

function escapeWifi(s = "") { return s.replace(/([\\;,:"])/g, "\\$1"); }

export function WifiQRTool() {
  const [ssid, setSsid] = useState("");
  const [password, setPassword] = useState("");
  const [security, setSecurity] = useState("WPA");
  const [hidden, setHidden] = useState(false);
  const payload = ssid
    ? `WIFI:T:${security === "nopass" ? "nopass" : security};S:${escapeWifi(ssid)};${security === "nopass" ? "" : `P:${escapeWifi(password)};`}${hidden ? "H:true;" : ""};`
    : "";

  const download = () => {
    trackClick("wifi-qr-download");
    const svg = document.querySelector("#wifi-qr-svg svg");
    if (!svg) return;
    const s = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([s], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `wifi-${ssid || "network"}.svg`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ToolShell
      slug="wifi-qr"
      title="Wi-Fi QR code generator"
      description="Generate a printable QR code that lets guests join your Wi-Fi with one scan — no typing the password. Runs entirely in your browser; nothing is sent to a server."
      canonical="https://speedtesttools.com/tools/wifi-qr"
      testid="wifi-qr-tool"
    >
      <div className="space-y-3">
        <Field label="Network name (SSID)">
          <input value={ssid} onChange={(e) => setSsid(e.target.value)} placeholder="MyHomeWiFi" className="input" data-testid="wifi-ssid-input" />
        </Field>
        <Field label="Password">
          <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={security === "nopass" ? "(no password)" : ""} className="input" disabled={security === "nopass"} data-testid="wifi-password-input" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Security">
            <select value={security} onChange={(e) => setSecurity(e.target.value)} className="input" data-testid="wifi-security-select">
              <option value="WPA">WPA / WPA2 / WPA3</option>
              <option value="WEP">WEP</option>
              <option value="nopass">Open (no password)</option>
            </select>
          </Field>
          <Field label="Hidden network">
            <button onClick={() => setHidden(!hidden)} className={`input flex items-center justify-between ${hidden ? "text-[var(--cobalt-bright)]" : ""}`}>
              {hidden ? "Yes" : "No"} <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </Field>
        </div>
      </div>

      {payload && (
        <div className="mt-6 pt-6 border-t border-white/8 flex flex-col items-center" data-testid="wifi-qr-output">
          <div id="wifi-qr-svg" className="p-4 bg-white rounded-lg">
            <QRCodeSVG value={payload} size={220} level="M" />
          </div>
          <p className="text-xs text-[var(--text-muted)] font-mono mt-3">Scan with any modern phone camera</p>
          <button onClick={download} className="btn-cobalt mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium" data-testid="wifi-qr-download-btn">
            <Download className="w-4 h-4" /> Download SVG
          </button>
        </div>
      )}
    </ToolShell>
  );
}

/* ==========================================================
 * DNS lookup
 * ==========================================================*/

export function DnsTool() {
  const [domain, setDomain] = useState("");
  const [record, setRecord] = useState("A");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (e) => {
    e?.preventDefault?.();
    if (!domain.trim() || busy) return;
    setBusy(true);
    trackClick("dns-lookup", { record });
    try {
      const r = await api.get("/tools/dns", { params: { domain: domain.trim(), record } });
      setResult(r.data);
    } catch (e2) {
      setResult({ error: e2?.response?.data?.detail || "Lookup failed" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <ToolShell
      slug="dns-lookup"
      title="DNS lookup"
      description="Resolve A, AAAA, MX, TXT, CNAME, NS and SOA records for any public domain. Uses Cloudflare and Google DNS."
      canonical="https://speedtesttools.com/tools/dns-lookup"
      testid="dns-tool"
    >
      <form onSubmit={run} className="space-y-3">
        <Field label="Domain">
          <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="example.com" className="input" data-testid="dns-domain-input" required />
        </Field>
        <Field label="Record type">
          <select value={record} onChange={(e) => setRecord(e.target.value)} className="input" data-testid="dns-record-select">
            {["A", "AAAA", "MX", "TXT", "CNAME", "NS", "SOA"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <button type="submit" disabled={busy} className="btn-cobalt w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium disabled:opacity-60" data-testid="dns-run-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />} Look it up
        </button>
      </form>
      {result && <ResultBox testid="dns-result">
        {result.error ? <ErrorLine>{result.error}</ErrorLine>
          : result.answers?.length ? <ul className="space-y-1 font-mono text-sm">
              {result.answers.map((a, i) => <li key={i} className="break-all"><span className="text-[var(--text-muted)]">{result.record}</span> {a}</li>)}
              {result.ttl != null && <li className="text-[11px] text-[var(--text-muted)] mt-2">TTL: {result.ttl}s</li>}
            </ul>
          : <ErrorLine>No {result.record} records found for {result.domain}.</ErrorLine>}
      </ResultBox>}
    </ToolShell>
  );
}

/* ==========================================================
 * WHOIS
 * ==========================================================*/

export function WhoisTool() {
  const [domain, setDomain] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e?.preventDefault?.();
    if (!domain.trim() || busy) return;
    setBusy(true);
    trackClick("whois-lookup");
    try {
      const r = await api.get("/tools/whois", { params: { domain: domain.trim() } });
      setResult(r.data);
    } catch (e2) {
      setResult({ error: e2?.response?.data?.detail || "Lookup failed" });
    } finally { setBusy(false); }
  };
  return (
    <ToolShell
      slug="whois"
      title="WHOIS lookup"
      description="Look up who owns any domain, when it was registered, when it expires, and its nameservers."
      canonical="https://speedtesttools.com/tools/whois"
      testid="whois-tool"
    >
      <form onSubmit={run} className="space-y-3">
        <Field label="Domain">
          <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="example.com" className="input" data-testid="whois-domain-input" required />
        </Field>
        <button type="submit" disabled={busy} className="btn-cobalt w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium disabled:opacity-60" data-testid="whois-run-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Look up owner
        </button>
      </form>
      {result && <ResultBox testid="whois-result">
        {result.error || !result.found ? <ErrorLine>{result.error || "No record found for this domain."}</ErrorLine> :
          <dl className="space-y-2 text-sm">
            <Row2 k="Registrar" v={result.registrar || "—"} />
            <Row2 k="Created" v={result.created ? result.created.slice(0, 10) : "—"} />
            <Row2 k="Updated" v={result.updated ? result.updated.slice(0, 10) : "—"} />
            <Row2 k="Expires" v={result.expires ? result.expires.slice(0, 10) : "—"} />
            <Row2 k="Status" v={(result.status || []).join(", ") || "—"} />
            <Row2 k="Nameservers" v={(result.nameservers || []).join(", ") || "—"} mono />
          </dl>}
      </ResultBox>}
    </ToolShell>
  );
}

/* ==========================================================
 * Port checker
 * ==========================================================*/

const COMMON_PORTS = [{ p: 22, l: "SSH" }, { p: 80, l: "HTTP" }, { p: 443, l: "HTTPS" }, { p: 3306, l: "MySQL" }, { p: 5432, l: "Postgres" }, { p: 6379, l: "Redis" }, { p: 25565, l: "Minecraft" }];

export function PortTool() {
  const [host, setHost] = useState("");
  const [port, setPort] = useState(443);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e?.preventDefault?.();
    if (!host.trim() || busy) return;
    setBusy(true);
    trackClick("port-check", { port });
    try {
      const r = await api.get("/tools/port", { params: { host: host.trim(), port } });
      setResult(r.data);
    } catch (e2) {
      setResult({ error: e2?.response?.data?.detail || "Check failed" });
    } finally { setBusy(false); }
  };
  return (
    <ToolShell
      slug="port-check"
      title="Open port checker"
      description="Test whether a TCP port is reachable from the public internet. Great for verifying game servers, web servers, or SSH."
      canonical="https://speedtesttools.com/tools/port-check"
      testid="port-tool"
    >
      <form onSubmit={run} className="space-y-3">
        <Field label="Host or IP">
          <input value={host} onChange={(e) => setHost(e.target.value)} placeholder="example.com or 1.1.1.1" className="input" data-testid="port-host-input" required />
        </Field>
        <div className="grid grid-cols-3 gap-3 items-end">
          <div className="col-span-1">
            <Field label="Port">
              <input type="number" min="1" max="65535" value={port} onChange={(e) => setPort(parseInt(e.target.value || "0", 10))} className="input" data-testid="port-num-input" />
            </Field>
          </div>
          <div className="col-span-2 flex gap-1 flex-wrap items-end pb-0.5">
            {COMMON_PORTS.map((p) => (
              <button key={p.p} type="button" onClick={() => setPort(p.p)} className="text-[11px] font-mono px-2 py-1 rounded border border-white/10 hover:bg-white/5 hover:border-[var(--cobalt)] transition">
                {p.p} <span className="text-[var(--text-muted)]">{p.l}</span>
              </button>
            ))}
          </div>
        </div>
        <button type="submit" disabled={busy} className="btn-cobalt w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium disabled:opacity-60" data-testid="port-run-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />} Check port
        </button>
      </form>
      {result && <ResultBox testid="port-result">
        {result.error && !result.ip ? <ErrorLine>{result.error}</ErrorLine> :
          <div className="space-y-1 font-mono text-sm">
            <div className="flex items-center gap-2">
              <span className={`dot ${result.open ? "dot-up" : "dot-down"}`} />
              <span className="text-white">Port {result.port} on {result.host} is <b className={result.open ? "text-emerald-300" : "text-rose-300"}>{result.open ? "OPEN" : "CLOSED"}</b></span>
            </div>
            {result.ip && <div className="text-[11px] text-[var(--text-muted)]">Resolved to {result.ip}</div>}
            {result.latency_ms != null && <div className="text-[11px] text-[var(--text-muted)]">Connect time: {result.latency_ms} ms</div>}
            {result.error && <div className="text-[11px] text-[var(--text-muted)]">Reason: {result.error}</div>}
          </div>}
      </ResultBox>}
    </ToolShell>
  );
}

/* ==========================================================
 * MAC vendor lookup
 * ==========================================================*/

export function MacTool() {
  const [mac, setMac] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e?.preventDefault?.();
    if (!mac.trim() || busy) return;
    setBusy(true);
    trackClick("mac-lookup");
    try {
      const r = await api.get("/tools/mac", { params: { mac: mac.trim() } });
      setResult(r.data);
    } catch (e2) {
      setResult({ error: e2?.response?.data?.detail || "Lookup failed" });
    } finally { setBusy(false); }
  };
  return (
    <ToolShell
      slug="mac-lookup"
      title="MAC address vendor lookup"
      description="Look up the hardware manufacturer for any MAC address. Handy for identifying unknown devices on your network."
      canonical="https://speedtesttools.com/tools/mac-lookup"
      testid="mac-tool"
    >
      <form onSubmit={run} className="space-y-3">
        <Field label="MAC address">
          <input value={mac} onChange={(e) => setMac(e.target.value)} placeholder="AA:BB:CC:11:22:33" className="input font-mono" data-testid="mac-input" required />
        </Field>
        <button type="submit" disabled={busy} className="btn-cobalt w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium disabled:opacity-60" data-testid="mac-run-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cpu className="w-4 h-4" />} Look up vendor
        </button>
      </form>
      {result && <ResultBox testid="mac-result">
        {result.error ? <ErrorLine>{result.error}</ErrorLine>
          : result.found ? <div><div className="text-xs text-[var(--text-muted)] mb-1">Vendor</div><div className="font-display text-xl">{result.vendor}</div></div>
          : <ErrorLine>No vendor found for {result.mac}.</ErrorLine>}
      </ResultBox>}
    </ToolShell>
  );
}

/* ==========================================================
 * Subnet calculator (pure JS)
 * ==========================================================*/

function ipToInt(ip) { return ip.split(".").reduce((s, o) => (s << 8) + parseInt(o, 10), 0) >>> 0; }
function intToIp(n) { return [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join("."); }

function subnetCalc(input) {
  const m = /^\s*(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\/(\d{1,2})\s*$/.exec(input);
  if (!m) return null;
  const ip = m[1];
  const bits = parseInt(m[2], 10);
  if (bits > 32) return null;
  const ipInt = ipToInt(ip);
  const mask = bits === 0 ? 0 : (0xffffffff << (32 - bits)) >>> 0;
  const network = (ipInt & mask) >>> 0;
  const broadcast = (network | (~mask >>> 0)) >>> 0;
  const total = 2 ** (32 - bits);
  const usable = bits >= 31 ? total : Math.max(0, total - 2);
  return {
    network: intToIp(network),
    broadcast: intToIp(broadcast),
    mask: intToIp(mask),
    firstHost: bits >= 31 ? intToIp(network) : intToIp(network + 1),
    lastHost: bits >= 31 ? intToIp(broadcast) : intToIp(broadcast - 1),
    totalHosts: total,
    usableHosts: usable,
    cidr: `${ip}/${bits}`,
  };
}

export function SubnetTool() {
  const [input, setInput] = useState("192.168.1.0/24");
  const result = subnetCalc(input);
  return (
    <ToolShell
      slug="subnet-calculator"
      title="IPv4 subnet calculator"
      description="Enter an IPv4 CIDR and get network, broadcast, mask, host range and total addresses. Runs 100% in your browser."
      canonical="https://speedtesttools.com/tools/subnet-calculator"
      testid="subnet-tool"
    >
      <Field label="CIDR block">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="192.168.1.0/24" className="input font-mono" data-testid="subnet-input" />
      </Field>
      {result ? (
        <ResultBox testid="subnet-result">
          <dl className="space-y-2 text-sm">
            <Row2 k="Network" v={result.network} mono />
            <Row2 k="Broadcast" v={result.broadcast} mono />
            <Row2 k="Subnet mask" v={result.mask} mono />
            <Row2 k="First host" v={result.firstHost} mono />
            <Row2 k="Last host" v={result.lastHost} mono />
            <Row2 k="Total addresses" v={result.totalHosts.toLocaleString()} mono />
            <Row2 k="Usable hosts" v={result.usableHosts.toLocaleString()} mono />
          </dl>
        </ResultBox>
      ) : (
        <ResultBox><ErrorLine>Enter a valid CIDR like <span className="font-mono">10.0.0.0/8</span></ErrorLine></ResultBox>
      )}
    </ToolShell>
  );
}

/* ==========================================================
 * Shared UI bits
 * ==========================================================*/

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-muted)] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
function ResultBox({ children, testid }) {
  return (
    <div className="mt-6 pt-6 border-t border-white/8" data-testid={testid}>
      {children}
    </div>
  );
}
function ErrorLine({ children }) { return <div className="text-sm text-rose-300">{children}</div>; }

function Row2({ k, v, mono }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText?.(String(v)); setCopied(true); setTimeout(() => setCopied(false), 1200); };
  return (
    <div className="flex items-center justify-between gap-3 group cursor-pointer" onClick={copy}>
      <span className="text-[var(--text-dim)] text-xs uppercase tracking-wider font-mono">{k}</span>
      <span className={`text-white ${mono ? "font-mono text-sm" : "text-sm"} inline-flex items-center gap-2`}>
        {v}
        {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3 text-[var(--text-muted)] opacity-0 group-hover:opacity-100 transition" />}
      </span>
    </div>
  );
}
